/**
 * insight.tsx — the two blocks REDESIGN puts at the top of the home screen, and the ONE row
 * component every urgency list uses (§4.3: "Each row: category/provider icon, a one-line title in
 * words, the date with a countdown chip, the amount, and an action link where one exists").
 *
 * Rules this file implements literally:
 *  - **LTR isolation is mandatory** (insight-contract §5.1): a narrative `num` part is rendered in
 *    its own `<bdi dir="ltr">`, never as the flattened `sentence.text`, or `₪28,450` and `10.09`
 *    reorder inside the RTL paragraph. `text` is kept for the `aria-label`.
 *  - **The narrative block is plain surface** — no card border, generous leading, full width
 *    (§4.1). It renders `headline` + `lines` in order, adds nothing and hides nothing.
 *  - **Empty buckets are absent** (§2): the caller iterates `urgency.groups`, which the server has
 *    already pruned. `השבוע` / `החודש` collapse to 3 rows with `עוד N`; `אפשר לחכות` is collapsed
 *    to one line and opens in place.
 *  - **Severity is state, not decoration** (§8): a late row gets the leading severity stripe
 *    (`fk-severity`, a logical border so RTL puts it on the right), never a full red card.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { ddmm } from '../../../../shared/insight/src/index.ts';
import type { Narrative, NarrativeSentence, UrgencyGroup, UrgencyItem } from '../../../../shared/insight/src/index.ts';
import { CategoryIcon, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import type { GlyphName } from '../../../../shared/ui/src/index.tsx';
import { URGENCY_GLYPH } from '../insight.ts';
import { fmtMoney } from '../format.ts';
import { he, providerName } from '../i18n/he.ts';
import { Link } from '../hooks/useRouter.tsx';
import { Icon } from './Icon.tsx';
import { Money, Num } from './ui.tsx';

// ───────────────────────────── §3 · the narrative ─────────────────────────────

/** One sentence, with every number in its own LTR isolate (insight-contract §5.1). */
function Sentence({ sentence }: { sentence: NarrativeSentence }) {
  return (
    <>
      {sentence.parts.map((part, index) =>
        part.kind === 'num' ? (
          <bdi key={index} dir="ltr" className="fk-num">
            {part.text}
          </bdi>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

/**
 * `מה קורה` — the block at the very top of the home screen (§4.1). Plain ground, no card, the
 * headline the only sentence at 18px and 0–3 supporting lines at 15px.
 */
export function NarrativeBlock({ narrative }: { narrative: Narrative }) {
  return (
    <section className="fk-plain narrative" aria-label={he.narrative.region}>
      <h2 className="narrative__kicker">{he.narrative.title}</h2>
      <p className="fk-lede" aria-label={narrative.headline.text}>
        <Sentence sentence={narrative.headline} />
      </p>
      {narrative.lines.map((line) => (
        <p className="fk-body" key={line.key} aria-label={line.text}>
          <Sentence sentence={line} />
        </p>
      ))}
    </section>
  );
}

// ───────────────────────────── §4.2 · the one hero figure ─────────────────────────────

/**
 * The single 36px+ figure of the screen (§8 "One hero per screen"), its inputs on one small line
 * beneath it and a chip that carries the comparison — §8 again: "Every number carries its unit and
 * its comparison", so the figure never ships without one.
 */
export function HeroFigure({
  label,
  value,
  inputs,
  chip,
  note,
}: {
  label: string;
  value: ReactNode;
  inputs?: ReactNode;
  chip?: ReactNode;
  note?: ReactNode;
}) {
  return (
    <section className="fk-plain hero-block" aria-label={label}>
      <div className="hero-block__lbl">{label}</div>
      <p className="fk-hero">{value}</p>
      <div className="hero-block__under">
        {inputs ? <span className="fk-hero-inputs">{inputs}</span> : null}
        {chip ?? null}
      </div>
      {note ? <p className="fk-meta hero-block__note">{note}</p> : null}
    </section>
  );
}

// ───────────────────────────── §4.3 · the urgency row ─────────────────────────────

function glyphOf(item: UrgencyItem): GlyphName {
  return (URGENCY_GLYPH[item.icon] ?? 'dashed') as GlyphName;
}

/** The chip tone of a countdown: late is critical, "landing now" is warn, the rest is neutral. */
function countdownTone(item: UrgencyItem): string {
  if (item.late) return 'critical';
  if (item.severity === 'critical') return 'critical';
  if (item.daysUntil !== null && item.daysUntil <= 3) return 'warn';
  return 'outline';
}

/**
 * ONE row component, used by `עכשיו`, `השבוע`, `החודש` and `אפשר לחכות` alike (§4.3/§4.4).
 *
 * The icon is the provider's mark when the item names one (REDESIGN §7a puts a mark on every
 * account, card, transaction and loan row) and the category glyph otherwise (§7b) — never a blank.
 */
export function UrgencyRow({ item, asOf }: { item: UrgencyItem; asOf: string }) {
  const stripe = item.late || item.severity === 'critical' ? ' fk-severity' : item.severity === 'warn' ? ' fk-severity fk-severity--warn' : '';
  const body = (
    <>
      <span className="urow__ic" aria-hidden="true">
        {item.providerId ? (
          <ProviderMark providerId={item.providerId} size={28} />
        ) : (
          <CategoryIcon glyph={glyphOf(item)} soft size={18} hue={item.severity === 'critical' ? 'warn' : undefined} />
        )}
      </span>
      <span className="urow__main">
        <span className="urow__title">{item.title}</span>
        <span className="urow__meta">
          {/* REDESIGN §3 `dd.mm`, zero-padded, from the SAME helper both systems' narratives use. */}
          {item.date ? <Num>{ddmm(item.date)}</Num> : null}
          {item.countdown ? (
            <span className={`chip sm ${countdownTone(item)}`}>
              <Icon name={item.late ? 'alertCircle' : 'clock'} />
              <span>{item.countdown}</span>
            </span>
          ) : null}
          {/* No second `באיחור`: the countdown chip above already reads `באיחור N ימים`, and §4.3's
              severity stripe carries the state — so the word was printing twice in one meta line. */}
        </span>
      </span>
      {item.amountAgorot === null ? <span className="urow__amt note">{he.app.none}</span> : (
        <span className="urow__amt">
          <Money agorot={item.amountAgorot} />
        </span>
      )}
      {item.actionHref ? (
        <span className="urow__go">
          <span className="desk-only">{he.urgency.action}</span>
          <Icon name="arrowLeft" className="sm" />
        </span>
      ) : null}
    </>
  );
  const className = `urow${stripe}${item.actionHref ? ' urow--link' : ''}`;
  const label = `${item.title}${item.countdown ? ` · ${item.countdown}` : ''}${
    item.amountAgorot === null ? '' : ` · ${fmtMoney(item.amountAgorot)}`
  }`;
  if (!item.actionHref) {
    return (
      <div className={className} aria-label={label} role="group">
        {body}
      </div>
    );
  }
  return (
    <Link className={className} to={item.actionHref} aria-label={label}>
      {body}
    </Link>
  );
}

/**
 * One bucket section: the heading, the rows, and `עוד N` when the bucket is longer than `limit`
 * (§4.4 — `השבוע` and `החודש` show 3 and expand in place). `עכשיו` passes no limit: what is urgent
 * is never hidden behind a toggle.
 */
export function UrgencySection({ group, asOf, limit }: { group: UrgencyGroup; asOf: string; limit?: number }) {
  const [expanded, setExpanded] = useState(false);
  const shown = limit && !expanded ? group.items.slice(0, limit) : group.items;
  const hidden = group.items.length - shown.length;
  return (
    <section className="fk-section urgency" aria-labelledby={`bucket-${group.bucket}`}>
      {/* No count badge: the rows are right there, personal shows none, and REDESIGN §0's
          complaint is the number count. Screen readers still get it from the heading's list. */}
      <div className="urgency__head">
        <h2 className="fk-section-title" id={`bucket-${group.bucket}`}>
          {group.label}
        </h2>
      </div>
      <div className="urows">
        {shown.map((item) => (
          <UrgencyRow key={item.id} item={item} asOf={asOf} />
        ))}
      </div>
      {hidden > 0 || (limit && expanded) ? (
        <button type="button" className="link-btn urgency__more" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? he.urgency.less : he.urgency.more(hidden)}
          <Icon name="chevDown" className={`sm later__chev${expanded ? ' later__chev--open' : ''}`} />
        </button>
      ) : null}
    </section>
  );
}

/**
 * `אפשר לחכות` — collapsed by default to a single line, `{n} דברים שאפשר לדחות · הצג`, opening in
 * place (§4.5). It is never a heading with rows already under it.
 */
export function LaterSection({ group, asOf }: { group: UrgencyGroup; asOf: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="fk-section later" aria-labelledby="bucket-later">
      <button type="button" className="later__toggle" aria-expanded={open} aria-controls="later-rows" onClick={() => setOpen((value) => !value)}>
        <Icon name="clock" className="sm" />
        <span id="bucket-later">{he.urgency.laterToggle(group.items.length)}</span>
        <span className="later__cta">
          {open ? he.urgency.hide : he.urgency.show}
          <Icon name="chevDown" className={`sm later__chev${open ? ' later__chev--open' : ''}`} />
        </span>
      </button>
      <div className="urows" id="later-rows" hidden={!open}>
        {group.items.map((item) => (
          <UrgencyRow key={item.id} item={item} asOf={asOf} />
        ))}
      </div>
    </section>
  );
}

// ───────────────────────────── §4.6 · the quiet detail strip ─────────────────────────────

/**
 * One block of the detail strip: a heading, at most five content lines and a link to its screen
 * (§4.6). Plain ground — a strip block is not something you act on, so it does not earn a card
 * (§8 "Cards are earned").
 */
export function StripBlock({
  title,
  to,
  linkLabel,
  children,
  icon,
}: {
  title: string;
  to: string;
  linkLabel: string;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <section className="fk-plain strip-block">
      <h3 className="strip-block__t">
        {icon ? (
          <span className="strip-block__ic" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {title}
      </h3>
      <div className="strip-block__body">{children}</div>
      <Link className="link-btn" to={to}>
        {linkLabel} <Icon name="arrowLeft" className="sm" />
      </Link>
    </section>
  );
}

/** One line of a strip block: a label and its figure. Never more than five per block (§4.6). */
export function StripLine({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="strip-line">
      <span className="strip-line__l">{label}</span>
      <span className="strip-line__v">{value}</span>
    </div>
  );
}

/** The provider mark + Hebrew name used wherever a connection or an account names its bank. */
export function ProviderTag({ providerId, size = 20 }: { providerId: string; size?: 20 | 28 | 40 }) {
  return (
    <span className="prov-tag">
      <ProviderMark providerId={providerId} size={size} />
      {providerName(providerId)}
    </span>
  );
}
